import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import ScrollReveal from './ScrollReveal';
import Seo from './Seo';

const SITE_URL = 'https://qcoda.com';

function LLMesh({ setPage }) {
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'LLMesh',
        applicationCategory: 'DeveloperApplication',
        operatingSystem: 'Linux, macOS, Windows',
        description:
            'Open-source distributed inference broker for local LLMs. One unified endpoint from dev to staging. Pool existing hardware. Built-in token analytics. OpenAI and Anthropic API compatible.',
        url: `${SITE_URL}/llmesh`,
        author: {
            '@type': 'Organization',
            name: 'QCoda',
            url: `${SITE_URL}/`,
        },
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
        },
        isPartOf: {
            '@type': 'SoftwareApplication',
            name: 'QCoda',
            url: `${SITE_URL}/`,
        },
    };

    return (
        <main>
            <Seo
                title="LLMesh — nginx for LLM Inference | QCoda"
                description="Open-source distributed inference broker for local LLMs. One endpoint from dev to staging. Pool your existing hardware. Built-in token analytics. OpenAI and Anthropic API compatible."
                path="/llmesh"
                jsonLd={jsonLd}
                jsonLdId="llmesh-jsonld"
            />

            {/* ===== HERO ===== */}
            <section className="site-section-lg">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={9} xl={7}>
                            <ScrollReveal animation="fade-in">
                                <span className="badge-ea mb-4 d-inline-block">Open Source</span>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <div className="d-flex align-items-center justify-content-center gap-3 mb-4">
                                    <img
                                        src="/llmesh_logo.png"
                                        alt="LLMesh"
                                        style={{ width: '62px', height: '62px', objectFit: 'contain' }}
                                    />
                                    <h1 className="hero-headline mb-0">
                                        LLMesh
                                    </h1>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={180}>
                                <p className="mb-4" style={{ fontSize: '1.3rem', fontWeight: 300, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.01em' }}>
                                    Think of it as nginx for LLM inference.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={250}>
                                <p className="hero-sub mx-auto mb-5">
                                    LLMesh is a distributed inference broker for local LLMs. One endpoint
                                    from dev to staging. Pool your existing hardware into a compute mesh.
                                    Built-in token analytics. OpenAI and Anthropic API compatible.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={400}>
                                <div className="d-flex gap-3 justify-content-center flex-wrap">
                                    <a
                                        href="https://github.com/qcoda-ai/llmesh"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-primary-cta"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }}>
                                            <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z"/>
                                        </svg>
                                        View on GitHub
                                    </a>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ===== SCREENSHOT ===== */}
            <section style={{ paddingBottom: '4rem' }}>
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <ScrollReveal animation="fade-up">
                                <div style={{
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255,255,255,0.13)',
                                    boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
                                }}>
                                    <img
                                        src="/llmesh_arena.gif"
                                        alt="LLMesh Model Arena — side-by-side model comparison running on local hardware"
                                        style={{ width: '100%', display: 'block' }}
                                    />
                                </div>
                                <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginTop: '0.75rem' }}>
                                    LLMesh Model Arena — run prompts against two local models simultaneously
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== THE PROBLEM ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-4">
                                    <span className="eyebrow-lead">The Problem</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-4 text-center">
                                    Your local LLM config shouldn't break when you push to staging.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3">
                                    Your local LLM setup works perfectly — fast, free, private. Then you push
                                    to staging and everything breaks, because your app was pointing at{' '}
                                    <code style={{ color: 'rgba(120,170,240,0.9)', fontSize: '0.95em' }}>localhost</code>.
                                    So you switch to a cloud API, pay for tokens you didn't want to pay for,
                                    and send data you didn't intend to share.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading">
                                    There's a better way. It just required building it.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ===== ARCHITECTURAL INSIGHT ===== */}
            <section className="site-section" style={{ paddingTop: 0 }}>
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="glass-card-lg">
                                    <p className="text-dim mb-2" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: 'rgba(255,255,255,0.4)' }}>
                                        The Insight
                                    </p>
                                    <p className="section-subheading mb-2">
                                        <strong style={{ color: 'rgba(255,255,255,0.9)' }}>Traditional:</strong>{' '}
                                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>app → fixed compute endpoint.</span>{' '}
                                        Change environment, break config.
                                    </p>
                                    <p className="section-subheading mb-0">
                                        <strong style={{ color: 'rgba(120,170,240,1)' }}>LLMesh:</strong>{' '}
                                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>compute nodes → fixed hub endpoint ← app.</span>{' '}
                                        Environment changes. Config doesn't.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== WHAT IT DOES ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">What LLMesh Does</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-3">
                                    Three things. No more, no less.
                                </h2>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="3" />
                                            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Environment Portability
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        One endpoint. Dev, staging, QA, a client demo on a borrowed laptop —
                                        same config, same call, same result. Nodes connect from wherever compute lives.
                                        The local-to-staging gap disappears.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                                            <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                                            <line x1="6" y1="6" x2="6.01" y2="6" />
                                            <line x1="6" y1="18" x2="6.01" y2="18" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Hardware Pooling
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Your existing machines become a distributed inference mesh. No new hardware
                                        required. Models register automatically from Ollama, vLLM, or MLX backends.
                                        You bring the hardware — LLMesh figures out the rest.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M3 3v18h18" />
                                            <path d="M18 17V9" />
                                            <path d="M13 17V5" />
                                            <path d="M8 17v-3" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Token Visibility
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Built-in analytics track inference events by owner, model, and time.
                                        Know what you're spending. Decide what goes where. LLMesh gives you the
                                        data — the routing decisions are yours.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== COMPATIBILITY ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Zero Friction</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-4 text-center">
                                    Works with what you already use.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3">
                                    LLMesh speaks OpenAI and Anthropic API formats natively. Point any compatible
                                    SDK, LiteLLM setup, or existing integration at the hub and it works.
                                    No middleware rewrites. No adapter layers.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading">
                                    The thing your app already does keeps working — it just routes through
                                    the mesh instead of straight to a cloud API.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== WHO IT'S FOR ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Who It's For</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-3">
                                    Built for anyone running serious local inference.
                                </h2>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={6}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="4 17 10 11 4 5" />
                                            <line x1="12" y1="19" x2="20" y2="19" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                                        AI &amp; SaaS Developers
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Building against local LLMs and tired of config breaking the moment
                                        you leave your laptop. LLMesh fixes the local-to-staging gap in an
                                        afternoon. No cloud contract required.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6}>
                            <ScrollReveal animation="fade-up" delay={100} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                                        Small Teams &amp; Offices
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Pool the machines you already have into shared inference capacity.
                                        Point ChatWise, Claude Code, or any OpenAI-compatible tool at the hub.
                                        Serious AI capability for general work — no DevOps hire, no monthly API bill.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6}>
                            <ScrollReveal animation="fade-up" delay={200} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                                        Privacy-Sensitive Organizations
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Legal, healthcare, finance, defense-adjacent — any environment where
                                        inference data leaving the building is a disqualifier. General internal
                                        tasks (drafting, research, summarization) on hardware you control, with
                                        nothing touching a third-party API unless you decide it should.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 2l1.8 5.6L19.5 9l-4.6 3.4L16.5 18 12 14.8 7.5 18l1.6-5.6L4.5 9l5.7-1.4z" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                                        AI-Native Teams Running Agents
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        When you're running agents at scale, inference volume is not trivial
                                        and the local-to-staging gap is not a minor inconvenience — it's a
                                        blocker. LLMesh was built for exactly this workload. It's what powers QCoda.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== ORIGIN / QCODA CONNECTION ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Why It Exists</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-4 text-center">
                                    We built it because we needed it.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3">
                                    LLMesh exists because{' '}
                                    <button
                                        onClick={() => setPage && setPage('home')}
                                        style={{ background: 'none', border: 'none', padding: 0, color: 'rgba(120,170,240,0.9)', fontWeight: 500, cursor: 'pointer', fontSize: 'inherit', lineHeight: 'inherit' }}
                                    >
                                        QCoda
                                    </button>{' '}
                                    needed it. QCoda runs AI agents across the full software development
                                    pipeline — from raw feature request to tested, human-approved code.
                                    At that scale, inference volume is not trivial, privacy is not optional,
                                    and the local-to-staging gap is a blocker.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading mb-3">
                                    We open-sourced it because every AI-native product team has the same
                                    problem and the same missing piece in their stack. Boring, reliable
                                    infrastructure — the kind nobody writes blog posts about but everybody
                                    depends on.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={300}>
                                <p className="section-subheading">
                                    If LLMesh solves your inference infrastructure problem, QCoda might
                                    solve what you're running on top of it.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== CTA ===== */}
            <section className="site-section text-center">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={7} xl={6}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-3">Get started in an afternoon.</h2>
                                <p className="section-subheading mb-5">
                                    Install via pip, connect your Ollama or vLLM backend, point your app at
                                    the hub. Your data stays on your hardware. Your config stays consistent.
                                </p>
                                <div className="d-flex gap-3 justify-content-center flex-wrap">
                                    <a
                                        href="https://github.com/qcoda-ai/llmesh"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-primary-cta"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }}>
                                            <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z"/>
                                        </svg>
                                        View on GitHub
                                    </a>
                                    <button
                                        onClick={() => setPage && setPage('contact')}
                                        className="btn-secondary-cta"
                                    >
                                        Talk to us about QCoda
                                    </button>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>
        </main>
    );
}

export default LLMesh;
